// TrafficChart.tsx
import React, { useEffect, useRef, useState } from "react";
import classNames from "classnames";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  type ChartOptions,
  type TooltipItem,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import { useSelector } from "react-redux";

import { useAppDispatch } from "../../redux";
import { getKonvasAICampaignStoreTrafficAction } from "../../redux/dashboard/analytics.slice";

import ExportData from "./export-data";
import Loader from "../../components/loader";
import NoData from "../../components/no-data";
import { useAnalyticsFilter } from "../../context/AnalyticsFilterContext";

import { handleExportPdf, handleExportPng } from "../../utils/functions";

interface StoreTrafficData {
  name: string;
  konvas: string;
}

// 1) Register the components we need
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const dateRange = [
  { name: "12 months", value: "12month" },
  { name: "30 days", value: "30days" },
  { name: "7days", value: "7days" },
  { name: "24 hours", value: "24hours" },
  { name: "Today", value: "12hours" },
];

const options: ChartOptions<"bar"> = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    title: {
      display: false,
      text: "Store traffic from Konvas AI campaigns",
    },
    legend: {
      display: false, // single dataset, so hide legend
    },
    tooltip: {
      callbacks: {
        // ctx typed as TooltipItem<'bar'>
        label: (ctx: TooltipItem<"bar">) => {
          const val = ctx.parsed.y as number;
          return `Traffic: ${val}`;
        },
      },
    },
  },
  scales: {
    x: {
      grid: { display: false },
    },
    y: {
      beginAtZero: true,
      ticks: {
        stepSize: 20,
      },
      grid: { color: "#e0e0e0" },
    },
  },
};

const TrafficChart: React.FC = () => {
  const dispatch = useAppDispatch();

  const {
    isFetchingKonvasAICampaignStoreTraffic,
    konvasAICampaignStoreTraffic,
  } = useSelector((state: any) => state.analyticsSlice);

  const { duration } = useAnalyticsFilter();

  const [currentDateRange, setCurrentDateRange] = useState(dateRange[0]?.value);

  const chartRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    dispatch(
      getKonvasAICampaignStoreTrafficAction({ range: currentDateRange })
    );
  }, [currentDateRange]);

  useEffect(() => {
    if (duration) setCurrentDateRange(duration);
  }, [duration]);

  const exportOptions = [
    {
      name: "PDF",
      action: () =>
        chartRef.current &&
        handleExportPdf(chartRef.current, "konvasAI-campaign-traffic.pdf"),
    },
    {
      name: "Image(PNG)",
      action: () =>
        chartRef.current &&
        handleExportPng(chartRef.current, "konvasAI-campaign-traffic.png"),
    },
  ];

  // 2) Month labels
  const labels = konvasAICampaignStoreTraffic?.map(
    (data: StoreTrafficData) => data?.name
  );

  const clicks = konvasAICampaignStoreTraffic?.map(
    (data: StoreTrafficData) => data?.konvas
  );

  const data = {
    labels,
    datasets: [
      {
        label: "Store traffic",
        data: clicks,
        backgroundColor: "#006AA0",
        borderRadius: 4, // rounded corners
        barPercentage: 0.8,
        categoryPercentage: 0.6,
        // barThickness: 64,      // <— exact width in px
        // maxBarThickness: 64,   // <— lock it there
      },
    ],
  };

  return (
    <div className="chart_section mt">
      <div className="chart_section_header">
        <p>Store traffic from Konvas AI campaigns</p>
        <ExportData size="small" exportOptions={exportOptions} />
      </div>
      <div className="date_range_select">
        {dateRange.map((range: { name: string; value: string }) => (
          <div
            className={classNames("date_range", {
              active: range?.value === currentDateRange,
            })}
            key={range?.value}
            onClick={() => setCurrentDateRange(range?.value)}
          >
            <p>{range?.name}</p>
          </div>
        ))}
      </div>
      {isFetchingKonvasAICampaignStoreTraffic ? (
        <Loader />
      ) : !konvasAICampaignStoreTraffic &&
        konvasAICampaignStoreTraffic?.length === 0 ? (
        <NoData text="No data" />
      ) : (
        <div className="chart_box" ref={chartRef}>
          <Bar data={data} options={options} />
        </div>
      )}
    </div>
  );
};

export default TrafficChart;
